import { Fragment } from 'react';
import { Link, LinkProps, useMatchRoute } from '@tanstack/react-router';
import { ChevronLeft, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/buttons/actionButtonVariants.ts';
import { Card } from '@/components/ui/Card.tsx';
import InitialsAvatar, {
  InitialsAvatarTone,
} from '@/components/ui/InitialsAvatar.tsx';
import { screenTitleClass } from '@/components/ui/typography.ts';

/**
 * Zakładka jako prawdziwy odnośnik — aktywną rozpoznaje router po adresie,
 * nagłówek nie porównuje identyfikatorów.
 */
export interface DetailsTabLink
  extends Pick<LinkProps, 'to' | 'params' | 'search'> {
  label: string;
  /**
   * Licznik przy etykiecie — ile osób czeka na liście rezerwowej, ile
   * wiadomości przyszło. Ekran podaje gotowy element, bo tylko on wie, czy
   * liczba już się doliczyła.
   */
  badge?: React.ReactNode;
}

/**
 * Zakładka opisana tak, żeby dało się z niej zrobić kafel menu mobilnego.
 * Ekrany, które schodzą poniżej `md` na kafle, podają zakładki w tej postaci.
 */
export interface DetailsTabMenuLink extends DetailsTabLink {
  icon: LucideIcon;
  /** Podpis pod nazwą na kaflu — po co tu wchodzić. */
  hint: string;
}

/**
 * Fakt w metryczce jako odnośnik — sygnatura rezerwacji, płatnik. Cel podajemy
 * literałem ścieżki i parametrami, nie sklejonym adresem.
 */
export interface DetailsMetaLink
  extends Pick<LinkProps, 'to' | 'params' | 'search'> {
  label: string;
}

/** Pozycja metryczki: sam tekst albo odnośnik na sąsiedni ekran. */
export type DetailsMetaItem = string | DetailsMetaLink;

interface DetailsHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Inicjały w awatarze: „BN”, „KB”, „RE”. */
  initials: string;
  avatarTone: InitialsAvatarTone;
  title: string;
  /** Odznaki przy tytule — tony wybiera ekran, nagłówek ich nie maluje. */
  badges?: React.ReactNode[];
  /**
   * Metryczka pod tytułem — kolejne fakty o rekordzie, rozdzielone kreską.
   * Ekran podaje je osobno, bo tylko wtedy któryś z nich może być odnośnikiem;
   * fakty złożone („wyjazd · kierunek”) zostają jednym napisem, bo czyta się je
   * razem.
   */
  meta: DetailsMetaItem[];
  /**
   * Zakładki ekranu jako odnośniki. Aktywną rozpoznaje router po adresie —
   * nagłówek nie porównuje identyfikatorów i nie zna zestawu z góry.
   */
  tabs: DetailsTabLink[];
  /**
   * Rząd tagów pod metryczką. Osobne miejsce, a nie kolejny fakt metryczki:
   * tagi zawijają się do wielu linii i mają własną kontrolkę dodawania.
   */
  tags?: React.ReactNode;
  /** Akcja po prawej stronie tożsamości, np. przełącznik trybu edycji. */
  actions?: React.ReactNode;
  /**
   * Pasek na całą szerokość pod zakładkami — miejsce na komunikat stanu
   * rekordu („Otwarty szkic paczki…”). Treść i barwę wnosi ekran, bo tylko on
   * wie, o jaki stan chodzi.
   */
  banner?: React.ReactNode;
  /** Rekord w centrum uwagi — bursztynowa ramka karty. */
  highlighted?: boolean;
  /**
   * Adres ekranu-menu, czyli goły adres rekordu bez segmentu zakładki.
   * Podany — poniżej `md` rząd zakładek ustępuje paskowi powrotu do kafli;
   * pominięty — rząd zostaje na każdej szerokości, tak jak dotąd.
   */
  menuLink?: Pick<LinkProps, 'to' | 'params' | 'search'>;
}

const tabClass =
  'inline-flex cursor-pointer items-center gap-2 border-0 bg-transparent py-[13px] text-[15px] font-semibold no-underline transition-colors';

/**
 * Podkreślenie aktywnej zakładki jest bursztynowe (`accent-600`), choć projekt
 * rysuje je w `ink` — świadomie, dla spójności z `ui/Tabs.tsx`, które w całej
 * aplikacji podkreśla aktywną zakładkę akcentem.
 */
const activeTabClass =
  'text-ink shadow-[inset_0_-2px_0_theme(colors.accent-600)]';

const inactiveTabClass = 'text-neutral-500 hover:text-neutral-700';

/** Ten sam odsyłacz między ekranami, co `BookingCodeLink` w module zmian. */
const metaLinkClass =
  'font-semibold text-info-300 underline-offset-2 hover:underline';

/**
 * Nagłówek ekranu rekordu: tożsamość, akcja trybu, zakładki i pasek stanu.
 * Bezstanowy — którą zakładkę podkreślić, rozstrzyga router po adresie.
 */
const DetailsHeader = ({
  initials,
  avatarTone,
  title,
  badges,
  meta,
  tabs,
  tags,
  actions,
  banner,
  highlighted = false,
  menuLink,
  className,
  ...props
}: DetailsHeaderProps) => {
  const matchRoute = useMatchRoute();
  const activeTab = tabs.find((tab) =>
    matchRoute({ to: tab.to, params: tab.params }),
  );

  return (
    <Card
      highlighted={highlighted}
      className={cn('overflow-hidden', className)}
      {...props}
    >
      <div className="flex flex-wrap items-center gap-3.5 px-4 py-4 sm:px-7 sm:py-5">
        <div className="flex min-w-0 flex-1 basis-60 flex-wrap items-center gap-x-3.5 gap-y-1.5">
          <InitialsAvatar initials={initials} tone={avatarTone} size="lg" />

          {/*
           * Poniżej `sm` awatar nie ma prawa trzymać własnej kolumny na całą
           * wysokość: zabierał metryczce i tagom ~60 px, przez co kod
           * rezerwacji łamał się na dwie linie tuż obok pustego miejsca.
           * `contents` rozpuszcza stos, więc awatar zostaje w linii tytułu,
           * a wiersze pod nim biorą pełną szerokość karty.
           */}
          <div className="flex min-w-0 flex-1 flex-col gap-1.5 max-sm:contents">
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3 sm:flex-none">
              {/* Na telefonie 22 px zostawiało obok awatara za mało miejsca
                  nawet na kod rezerwacji — łamał się na dwie linie. */}
              <span className={cn(screenTitleClass, 'max-sm:text-[19px]')}>
                {title}
              </span>
              {badges?.map((badge, index) => (
                <Fragment key={index}>{badge}</Fragment>
              ))}
            </div>

            <div className="flex w-full flex-wrap items-center gap-x-3.5 text-sm text-neutral-500 sm:w-auto">
              {meta.map((item, index) => (
                <Fragment key={typeof item === 'string' ? item : item.label}>
                  {index > 0 && (
                    <span
                      aria-hidden
                      className="hidden h-[13px] w-px bg-neutral-200 sm:block"
                    />
                  )}
                  {typeof item === 'string' ? (
                    <span>{item}</span>
                  ) : (
                    <Link
                      to={item.to}
                      params={item.params}
                      search={item.search}
                      className={metaLinkClass}
                    >
                      {item.label}
                    </Link>
                  )}
                </Fragment>
              ))}
            </div>

            {tags && <div className="w-full sm:mt-1 sm:w-auto">{tags}</div>}
          </div>
        </div>

        {/*
         * Na telefonie akcje dostają własny, pełny wiersz — dociśnięte do
         * prawej krawędzi wyglądały jak zgubiona resztka poprzedniego.
         */}
        {actions && (
          <div className="w-full shrink-0 sm:ml-auto sm:w-auto">{actions}</div>
        )}
      </div>

      <div
        className={cn(
          'flex-wrap gap-x-[26px] border-t border-neutral-100 px-4 sm:px-7',
          menuLink ? 'hidden md:flex' : 'flex',
        )}
      >
        {tabs.map((tab) => (
          <Link
            key={tab.label}
            to={tab.to}
            params={tab.params}
            search={tab.search}
            // Zakładka jest aktywna od samego adresu — parametry wyszukiwania
            // (np. tryb edycji) nie mają prawa jej wygaszać.
            activeOptions={{ includeSearch: false }}
            className={tabClass}
            activeProps={{ className: activeTabClass, 'aria-current': 'page' }}
            inactiveProps={{ className: inactiveTabClass }}
          >
            {tab.label}
            {tab.badge}
          </Link>
        ))}
      </div>

      {/*
       * Dziewięć zakładek zawijało się na telefonie w trzy piętra i spychało
       * treść poza ekran. Zamiast ściskać rząd, zakładki przenoszą się na
       * ekran-menu pod gołym adresem rekordu, a tutaj zostaje powrót do niego.
       */}
      {menuLink && activeTab && (
        <Link
          to={menuLink.to}
          params={menuLink.params}
          search={menuLink.search}
          className="flex items-center gap-2.5 border-t border-neutral-100 px-4 py-2.5 no-underline md:hidden"
        >
          {/*
           * `cn` wokół `buttonVariants` nie jest ozdobą: `iconOnly` dokłada
           * `p-0`, ale poziomy padding rozmiaru jest w arkuszu późniejszy
           * i bez scalenia klas wygrywa — znak zostawał ściśnięty do kilku
           * pikseli w środku pustego kwadratu. `ActionButton` scala tak samo.
           * To zostaje: konflikt siedzi w samej recepturze, nie w arkuszu
           * usuniętej paczki.
           */}
          <span
            className={cn(
              buttonVariants({ tone: 'outline', size: 'md', iconOnly: true }),
            )}
          >
            <ChevronLeft aria-hidden />
          </span>
          <span className="min-w-0 flex-1 truncate text-[15px] font-semibold text-ink">
            {activeTab.label}
          </span>
        </Link>
      )}

      {banner}
    </Card>
  );
};

export default DetailsHeader;
